/**
 * V2FlexibleSectionsSection — V2-native editor section for FlexibleLayout screens
 *
 * Renders in the standard OnboardingScreenEditorDialogV2 right panel.
 * Provides two mechanisms:
 *
 *   1. Compact structural list (top area):
 *      - Add current FlexibleLayout section kinds, including paywall, embed, input, and spacer sections
 *      - Reorder via DnD (@dnd-kit)
 *      - Delete section
 *      - Click a row to SELECT it
 *
 *   2. Content editor (bottom area):
 *      - Full V2 section editor for the SELECTED section via FlexibleSectionContentAdapter
 *      - Dynamically switches when a different section is selected
 *      - Updates are path-based (onChange('content.flexibleLayout.sections', ...)) so the
 *        phone preview in OnboardingScreenEditorDialogV2 refreshes in real-time.
 *
 * Reuses: FlexibleSectionContentAdapter, @dnd-kit (already installed)
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import TuneIcon from '@mui/icons-material/Tune';
import { useSortable } from '@dnd-kit/sortable';
import type { LayoutSection, SectionLayout } from '@segmently/schema-models/v2';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { FlowDocument } from '@segmently/schema-models/v2';
import { InlineSectionEditor } from './InlineSectionEditor';
import { SectionRowCard, getSectionDisplayLabel } from './SectionRowCard';
import { SectionsListManager, type SectionMenuOption } from './SectionsListManager';
import { PaddingElement, type PaddingValues } from '../../../factories/elements';
import { StyleFieldRow } from '../../shared/styleFields/StyleFieldRow';
import type { FieldConfig } from '../../../types/editorFieldTypes';
import { FIELD_STYLE_GRID_GAP } from '../../../config/ui';
import {
  WIDTH_KIND_OPTIONS,
  SECTION_SELF_ALIGNMENT_OPTIONS,
  getWidthValueOptions,
} from '../../../config/sectionLayoutSettingsConfig';
import type { AddMenuKind } from '../../../config/flexibleSectionDefaults';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { GuideAnchorHelp, WithGuideHelp } from '@modules/ui-components';
import { useEditorGuideService } from '@modules/reactFlow/contexts';
import { useEditorPresentationProfile } from './PresentationProfileContext';

const FLEXIBLE_SECTIONS_SECTION_TEST_ID = 'section-flexible-sections';

// ── Screen-level "Default section layout" (v2.19.0) ────────────────────────
// Writes content.flexibleLayout.defaultSectionLayout.* — shallow-merged UNDER
// every section's own layout at render time (section-set fields always win).
const DEFAULT_LAYOUT_BASE = 'content.flexibleLayout.defaultSectionLayout';

const DEFAULT_LAYOUT_WIDTH_FIELDS: FieldConfig[] = [
  {
    path: `${DEFAULT_LAYOUT_BASE}.widthKind`,
    label: 'Width mode',
    type: 'select',
    options: [...WIDTH_KIND_OPTIONS],
    defaultValue: undefined,
    isNullable: true,
  },
  {
    path: `${DEFAULT_LAYOUT_BASE}.widthValue`,
    label: 'Width value',
    type: 'numberCombo',
    options: getWidthValueOptions('fixed'),
    defaultValue: 100,
    defaultScrollValue: 100,
    isNullable: true,
  },
  {
    path: `${DEFAULT_LAYOUT_BASE}.maxWidth`,
    label: 'Max width (px)',
    type: 'numberCombo',
    options: getWidthValueOptions('fixed'),
    defaultValue: undefined,
    defaultScrollValue: 480,
    isNullable: true,
  },
  {
    path: `${DEFAULT_LAYOUT_BASE}.minWidth`,
    label: 'Min width (px)',
    type: 'numberCombo',
    options: getWidthValueOptions('fixed'),
    defaultValue: undefined,
    defaultScrollValue: 0,
    isNullable: true,
  },
  {
    path: `${DEFAULT_LAYOUT_BASE}.sectionHorizontalAlignment`,
    label: 'Self align',
    type: 'select',
    options: [...SECTION_SELF_ALIGNMENT_OPTIONS],
    defaultValue: undefined,
    isNullable: true,
  },
];
const FLEXIBLE_SECTION_MANAGEMENT_GUIDES = {
  addSection: 'screenedit-flexible-sections-add-section',
  screenSettings: 'screenedit-flexible-sections-screen-settings',
  screenScrollable: 'screenedit-flexible-sections-screen-scrollable',
  containerPadding: 'screenedit-flexible-sections-container-padding',
  reorderSections: 'screenedit-flexible-sections-reorder-sections',
  sectionName: 'screenedit-flexible-sections-section-name',
  deleteSection: 'screenedit-flexible-sections-delete-section',
  openSectionSettings: 'screenedit-flexible-sections-open-section-settings',
} as const;

// ─── Section kind metadata ─────────────────────────────────────────────────

/**
 * Options for the add-section dropdown menu. Includes PurchaseButton as virtual kind.
 * Background is NOT here — it's a screen-level section rendered by V2BackdropSection
 * (added to v2ScreenTypeConfig.FlexibleLayout.sections as 'backdrop').
 */
const ADD_MENU_OPTIONS: SectionMenuOption[] = [
  { value: 'Header', label: 'Header bar' },
  { value: 'Footer', label: 'Footer / Action bar' },
  { value: 'PaywallHeader', label: 'Paywall Header' },
  { value: 'Text', label: 'Text' },
  { value: 'Media', label: 'Media (image / video)' },
  { value: 'ProductCatalog', label: 'Product Catalog' },
  { value: 'Button', label: 'Button' },
  { value: 'PurchaseButton', label: 'Purchase Button' },
  { value: 'SingleSelectionList', label: 'Single Selection List' },
  { value: 'MultipleSelectionList', label: 'Multiple Selection List' },
  { value: 'BulletList', label: 'Bullet List' },
  { value: 'CustomEmbed', label: 'Custom Embed (HTML)' },
  { value: 'TextField' as AddMenuKind, label: 'Text Field' },
  { value: 'EmptySpace' as AddMenuKind, label: 'Empty Space' },
];

/**
 * Spec Issue #16 / D-10: the first article block kind offered in Studio — ONLY in article documents, and listed
 * FIRST above the funnel kinds. The funnel / onboarding / strategy palettes (ADD_MENU_OPTIONS) never offer it.
 */
const ARTICLE_ADD_MENU_OPTIONS: SectionMenuOption[] = [
  { value: 'ArticleIndex' as AddMenuKind, label: 'Article index' },
];

// ─── Compact sortable row ──────────────────────────────────────────────────

interface SortableCompactRowProps {
  section: LayoutSection;
  sections: LayoutSection[];
  locale: string;
  onDeleteRequest: (id: string, label: string) => void;
  onLayoutChange: (sectionId: string, updatedLayout: SectionLayout) => void;
  onLabelChange: (sectionId: string, newLabel: string) => void;
  writeSections: (updated: LayoutSection[]) => void;
  baseStep: FlowStepNode;
  disabled?: boolean;
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
  availableLocales?: string[];
  flowDocument?: FlowDocument | null;
  /** Step-level onChange for screen-level section kinds (Header, Footer) */
  onContentChange?: (path: string, value: unknown) => void;
}

const SortableCompactRow: React.FC<SortableCompactRowProps> = ({
  section,
  sections,
  locale,
  onDeleteRequest,
  onLayoutChange,
  onLabelChange,
  writeSections,
  baseStep,
  disabled,
  projectId,
  onboardingId,
  versionId,
  availableLocales,
  flowDocument,
  onContentChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  // Parent link chip for buttons linked to a section
  const parentChip = section.parentSectionId ? (() => {
    const parent = sections.find(s => s.id === section.parentSectionId);
    return parent ? (
      <Chip
        label={`→ ${parent.label || parent.kind}`}
        size="small"
        variant="outlined"
        sx={{ fontSize: 9, height: 18, flexShrink: 0, maxWidth: 100 }}
      />
    ) : null;
  })() : null;

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
      }}
    >
      <SectionRowCard
        section={section}
        displayLabel={section.label ?? getSectionDisplayLabel(section)}
        onLabelChange={onLabelChange}
        onDeleteRequest={onDeleteRequest}
        disabled={disabled}
        outerSx={{ opacity: isDragging ? 0.4 : 1 }}
        dragHandle={
          <Box
            {...attributes}
            {...listeners}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{ cursor: 'grab', color: 'text.disabled', display: 'flex', flexShrink: 0 }}
          >
            <DragHandleIcon fontSize="small" />
          </Box>
        }
        extraChips={parentChip}
        guideKeys={{
          reorder: FLEXIBLE_SECTION_MANAGEMENT_GUIDES.reorderSections,
          sectionName: FLEXIBLE_SECTION_MANAGEMENT_GUIDES.sectionName,
          deleteSection: FLEXIBLE_SECTION_MANAGEMENT_GUIDES.deleteSection,
          openSectionSettings: FLEXIBLE_SECTION_MANAGEMENT_GUIDES.openSectionSettings,
        }}
        renderExpandedContent={() => (
          <InlineSectionEditor
            section={section}
            sections={sections}
            writeSections={writeSections}
            onLayoutChange={onLayoutChange}
            locale={locale}
            baseStep={baseStep}
            disabled={disabled}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            availableLocales={availableLocales}
            flowDocument={flowDocument}
            onContentChange={onContentChange}
          />
        )}
      />
    </Box>
  );
};

// ─── Props ────────────────────────────────────────────────────────────────

export interface V2FlexibleSectionsSectionProps {
  /** V2 StepNode being edited */
  step: FlowStepNode;
  /** Current editing locale */
  locale: string;
  /** Path-based update callback — same pattern as all V2 native sections */
  onChange: (path: string, value: unknown) => void;
  /** Disables all inputs while saving */
  disabled?: boolean;
  /** Whether accordion starts expanded */
  defaultExpanded?: boolean;
  /** Project ID for upload service */
  projectId?: string;
  /** Onboarding ID for upload service */
  onboardingId?: string;
  /** Version ID for upload service */
  versionId?: string;
  /** Available locales for multi-locale upload dialog */
  availableLocales?: string[];
  /** Full FlowDocument for variable resolution */
  flowDocument?: FlowDocument | null;

  /** Theme settings for themed defaults on section creation */
  themeSettingsV2?: OnboardingThemeSettingsV2 | null;
}

// ─── Component ────────────────────────────────────────────────────────────

export const V2FlexibleSectionsSection: React.FC<V2FlexibleSectionsSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  onboardingId,
  versionId,
  availableLocales,
  flowDocument,
  themeSettingsV2,
}) => {
  const [screenSettingsOpen, setScreenSettingsOpen] = useState(false);
  const guideService = useEditorGuideService();

  // Article documents only (metadata.kind === 'article'): the palette gains "Article index". The published HTML
  // renders only the launch screen, and the validator refuses an ArticleIndex elsewhere — so offer it there only.
  const isArticleDocument = (flowDocument?.metadata as { kind?: unknown } | undefined)?.kind === 'article';
  const offerArticleKinds = isArticleDocument && step.id === flowDocument?.launchScreenId;

  // Read flexibleLayout config from step
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const config = (step.content as any)?.flexibleLayout as
    | {
        scrollable: boolean;
        sections: LayoutSection[];
        insets?: { top?: number; end?: number; bottom?: number; start?: number };
        defaultSectionLayout?: SectionLayout;
      }
    | undefined;

  const sections = useMemo(
    () => [...(config?.sections ?? [])].sort((a, b) => a.order - b.order),
    [config?.sections],
  );

  const scrollable = config?.scrollable ?? true;

  // ── Container padding helpers ──────────────────────────────────────

  const containerPaddingValues = useMemo<PaddingValues>(() => {
    const insets = config?.insets;
    return {
      paddingTop: typeof insets?.top === 'number' ? insets.top : 0,
      paddingRight: typeof insets?.end === 'number' ? insets.end : 0,
      paddingBottom: typeof insets?.bottom === 'number' ? insets.bottom : 0,
      paddingLeft: typeof insets?.start === 'number' ? insets.start : 0,
    };
  }, [config?.insets]);

  const handleContainerPaddingChange = useCallback(
    (path: string, value: number) => {
      // Translate PaddingElement paths → EdgeInsets paths
      const translated = path
        .replace('.paddingTop', '.top')
        .replace('.paddingRight', '.end')
        .replace('.paddingBottom', '.bottom')
        .replace('.paddingLeft', '.start');
      onChange(translated, value);
    },
    [onChange],
  );

  // ── Default section layout (v2.19.0) ───────────────────────────────

  const defaultSectionLayout = config?.defaultSectionLayout as
    | (SectionLayout & Record<string, unknown>)
    | undefined;

  // Active presentation profile (v2.19.1): in a non-base profile the block
  // reads/writes defaultSectionLayout.presentationOverrides.{profile}.* —
  // write routing happens in the dialog's handleContentChange; here we only
  // resolve the effective display value and the override badges.
  const activeProfile = useEditorPresentationProfile();

  const defaultLayoutProfileOverrides = useMemo(
    () =>
      activeProfile === 'base'
        ? undefined
        : ((defaultSectionLayout?.presentationOverrides as
            | Record<string, Record<string, unknown>>
            | undefined)?.[activeProfile]),
    [defaultSectionLayout, activeProfile],
  );

  const defaultLayoutInsetsValues = useCallback(
    (key: 'padding' | 'margin'): PaddingValues => {
      const base = defaultSectionLayout?.[key] as
        | { top?: number; end?: number; bottom?: number; start?: number }
        | undefined;
      const override = defaultLayoutProfileOverrides?.[key] as
        | Record<string, unknown>
        | undefined;
      const insets: Record<string, unknown> = { ...base, ...override };
      return {
        paddingTop: typeof insets.top === 'number' ? insets.top : 0,
        paddingRight: typeof insets.end === 'number' ? insets.end : 0,
        paddingBottom: typeof insets.bottom === 'number' ? insets.bottom : 0,
        paddingLeft: typeof insets.start === 'number' ? insets.start : 0,
      };
    },
    [defaultSectionLayout, defaultLayoutProfileOverrides],
  );

  /** Override value for the active profile, or undefined when not overridden. */
  const readDefaultOverrideValue = useCallback(
    (path: string): unknown => {
      const key = path.slice(DEFAULT_LAYOUT_BASE.length + 1);
      return defaultLayoutProfileOverrides?.[key];
    },
    [defaultLayoutProfileOverrides],
  );

  /** Effective field value: profile override first, then base default. */
  const readDefaultLayoutValue = useCallback(
    (path: string): unknown => {
      const overrideValue = readDefaultOverrideValue(path);
      if (overrideValue !== undefined) return overrideValue;
      if (!defaultSectionLayout) return undefined;
      const key = path.slice(DEFAULT_LAYOUT_BASE.length + 1);
      return (defaultSectionLayout as Record<string, unknown>)[key];
    },
    [defaultSectionLayout, readDefaultOverrideValue],
  );

  const currentDefaultWidthKind = readDefaultLayoutValue(
    `${DEFAULT_LAYOUT_BASE}.widthKind`,
  ) as string | undefined;

  const visibleDefaultWidthFields = useMemo(
    () =>
      DEFAULT_LAYOUT_WIDTH_FIELDS.filter((f) => {
        // widthValue is meaningful only for fixed/percent width modes
        if (f.path.endsWith('.widthValue')) {
          return currentDefaultWidthKind === 'fixed' || currentDefaultWidthKind === 'percent';
        }
        return true;
      }).map((f) =>
        f.path.endsWith('.widthValue')
          ? { ...f, options: getWidthValueOptions(currentDefaultWidthKind) }
          : f,
      ),
    [currentDefaultWidthKind],
  );

  // ── Section helpers ──────────────────────────────────────────────────

  /** Write updated sections array back via path-based onChange */
  const writeSections = useCallback(
    (updatedSections: LayoutSection[]) => {
      if (!config) {
        // First-ever section: initialize the whole config
        onChange('content.flexibleLayout', {
          scrollable: true,
          sections: updatedSections,
        });
      } else {
        onChange('content.flexibleLayout.sections', updatedSections);
      }
    },
    [config, onChange],
  );

  /** Update a single section's label — rebuilds the full array and writes atomically */
  const handleLabelChange = useCallback(
    (sectionId: string, newLabel: string) => {
      const updated = sections.map((s) =>
        s.id === sectionId ? { ...s, label: newLabel } : s,
      );
      writeSections(updated);
    },
    [sections, writeSections],
  );

  /** Clean up step-level content when a Header/Footer section is deleted */
  const handleSectionDeleted = useCallback(
    (deletedSection: LayoutSection) => {
      if (deletedSection.kind === 'Header') {
        onChange('content.header', null);
      } else if (deletedSection.kind === 'Footer') {
        onChange('content.actionBar', null);
      }
    },
    [onChange],
  );

  /** Initialize step-level content when a Header/Footer section is added */
  const handleSectionAdded = useCallback(
    (addedSection: LayoutSection) => {
      if (addedSection.kind === 'Header') {
        const headerData = themeSettingsV2?.headerBar
          ? structuredClone(themeSettingsV2.headerBar)
          : {
              back: { kind: 'icon', content: { translations: {}, appearance: {} }, appearance: {}, action: { kind: 'retreat', edges: [] } },
              skip: { kind: 'label', content: { translations: { [locale]: 'SKIP' }, kind: 'static', appearance: {} }, appearance: {}, action: { kind: 'advance', edges: [] } },
            };
        onChange('content.header', headerData);
      } else if (addedSection.kind === 'Footer') {
        const actionBarData = themeSettingsV2?.actionBar
          ? structuredClone(themeSettingsV2.actionBar)
          : {
              primary: {
                kind: 'label',
                content: { translations: { [locale]: 'Continue' }, kind: 'static', appearance: { fontFamily: 'SF Pro', fontSize: 17, fontWeight: 600, align: 'center' } },
                appearance: { backgroundColor: '#ffc201', fullWidth: true, borders: { radius: { uniform: 12 } } },
                action: { kind: 'advance', edges: [] },
              },
            };
        onChange('content.actionBar', actionBarData);
      }
    },
    [onChange, locale, themeSettingsV2],
  );

  /** Update a single section's layout — rebuilds the full array and writes atomically */
  const handleLayoutChange = useCallback(
    (sectionId: string, updatedLayout: SectionLayout) => {
      const updated = sections.map((s) =>
        s.id === sectionId ? { ...s, layout: updatedLayout } : s,
      );
      writeSections(updated);
    },
    [sections, writeSections],
  );

  const renderGuideLabel = useCallback(
    (guideKey: string, label: string): React.ReactNode => {
      const labelNode = (
        <Typography variant="body2" color="text.secondary" component="span">
          {label}
        </Typography>
      );

      if (!guideService) return labelNode;

      return (
        <WithGuideHelp
          guideKey={guideKey}
          guideService={guideService}
          data-testid={`guide-anchor-${guideKey}`}
          position="inline-end"
          iconSize="small"
          stopClickPropagation
        >
          {labelNode}
        </WithGuideHelp>
      );
    },
    [guideService],
  );

  const withGuide = useCallback(
    (
      guideKey: string,
      children: React.ReactNode,
      position: 'top-right' | 'top-left' | 'inline-end' = 'top-left',
    ): React.ReactNode => {
      if (!guideService) return children;

      return (
        <WithGuideHelp
          guideKey={guideKey}
          guideService={guideService}
          data-testid={`guide-anchor-${guideKey}`}
          position={position}
          iconSize="small"
          stopClickPropagation
        >
          {children}
        </WithGuideHelp>
      );
    },
    [guideService],
  );

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <Box data-testid={FLEXIBLE_SECTIONS_SECTION_TEST_ID}>
      {/* ── Title row: "Sections" + settings gear ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="h6" noWrap>
            Sections ({sections.length})
          </Typography>
          <GuideAnchorHelp ownerTestId={FLEXIBLE_SECTIONS_SECTION_TEST_ID} />
        </Box>
        {withGuide(
          FLEXIBLE_SECTION_MANAGEMENT_GUIDES.screenSettings,
          (
            <IconButton
              size="small"
              onClick={() => setScreenSettingsOpen((prev) => !prev)}
              data-testid="flex-screen-settings-gear"
              sx={{
                color: screenSettingsOpen ? 'primary.main' : 'action.active',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <TuneIcon sx={{ fontSize: 18 }} />
            </IconButton>
          ),
        )}
      </Box>

      {/* ── Screen-level settings (toggled by gear) ── */}
      <Collapse in={screenSettingsOpen} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2, pb: 1 }}>
          <FormControlLabel
            label={renderGuideLabel(FLEXIBLE_SECTION_MANAGEMENT_GUIDES.screenScrollable, 'Scrollable screen')}
            control={
              <Switch
                size="small"
                checked={scrollable}
                disabled={disabled}
                data-testid="flex-screen-scrollable-toggle"
                onChange={(e) => onChange('content.flexibleLayout.scrollable', e.target.checked)}
              />
            }
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {scrollable
              ? 'Sections stack vertically with scroll'
              : 'Sections fill screen height using flexGrow/flexShrink'}
          </Typography>

          {/* Container-level padding (EdgeInsets on FlexibleLayoutConfig.insets) */}
          <Box sx={{ mt: 1.5 }} data-testid="flex-screen-container-padding">
            <PaddingElement
              basePath="content.flexibleLayout.insets"
              values={containerPaddingValues}
              onChange={handleContainerPaddingChange}
              label="Container padding"
              guideKey={FLEXIBLE_SECTION_MANAGEMENT_GUIDES.containerPadding}
              guideService={guideService ?? undefined}
              disabled={disabled}
              defaultExpanded={false}
            />
          </Box>

          {/* Default section layout (v2.19.0): screen-level width/padding/margin
            * defaults, shallow-merged under each section's own layout */}
          <Box sx={{ mt: 1.5 }} data-testid="flex-screen-default-section-layout">
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.25 }}>
              Default section layout
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Applies to every section that has no own value; a section&apos;s own
              settings always win.
            </Typography>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: FIELD_STYLE_GRID_GAP, mb: 1.5 }}
              data-testid="flex-screen-default-layout-fields"
            >
              {visibleDefaultWidthFields.map((fieldConfig) => {
                const fieldKey = fieldConfig.path.split('.').pop();
                const hasProfileOverride =
                  readDefaultOverrideValue(fieldConfig.path) !== undefined;
                return (
                  <Box key={fieldConfig.path} sx={{ position: 'relative' }}>
                    <StyleFieldRow
                      config={fieldConfig}
                      value={readDefaultLayoutValue(fieldConfig.path)}
                      onChange={(value) => onChange(fieldConfig.path, value)}
                      disabled={disabled}
                      inputTestId={`input-default-layout-${fieldKey}`}
                      checkboxTestId={`checkbox-default-layout-${fieldKey}`}
                    />
                    {hasProfileOverride && (
                      <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={activeProfile.toUpperCase()}
                        onDelete={() => onChange(fieldConfig.path, null)}
                        data-testid={`default-layout-override-badge-${fieldKey}`}
                        sx={{
                          height: 16,
                          fontSize: '0.55rem',
                          mt: 0.25,
                          '& .MuiChip-deleteIcon': { fontSize: 12 },
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
            <PaddingElement
              basePath={`${DEFAULT_LAYOUT_BASE}.padding`}
              values={defaultLayoutInsetsValues('padding')}
              onChange={handleContainerPaddingChange}
              label="Default section padding"
              guideService={guideService ?? undefined}
              disabled={disabled}
              defaultExpanded={false}
              data-testid="flex-screen-default-section-padding"
            />
            <PaddingElement
              basePath={`${DEFAULT_LAYOUT_BASE}.margin`}
              values={defaultLayoutInsetsValues('margin')}
              onChange={handleContainerPaddingChange}
              label="Default section margin"
              guideService={guideService ?? undefined}
              disabled={disabled}
              defaultExpanded={false}
              min={-100}
              data-testid="flex-screen-default-section-margin"
            />
          </Box>
        </Box>
      </Collapse>

      <Divider sx={{ mt: 0, mb: 2 }} />

      {/* ── Sections list (reusable manager with DnD + delete confirmation) ── */}
      <SectionsListManager
        sections={sections}
        writeSections={writeSections}
        locale={locale}
        menuOptions={offerArticleKinds ? [...ARTICLE_ADD_MENU_OPTIONS, ...ADD_MENU_OPTIONS] : ADD_MENU_OPTIONS}
        articleDefaultLanguage={isArticleDocument ? flowDocument?.defaultLanguage : undefined}
        title="SECTIONS"
        menuTitle="Add section"
        sortable
        confirmDelete
        hideHeader
        emptyMessage="No sections yet. Click + Add to create one."
        disabled={disabled}
        themeSettingsV2={themeSettingsV2}
        onSectionDeleted={handleSectionDeleted}
        onSectionAdded={handleSectionAdded}
        addGuideKey={FLEXIBLE_SECTION_MANAGEMENT_GUIDES.addSection}
        renderRow={(section, onDeleteRequest) => (
          <SortableCompactRow
            key={section.id}
            section={section}
            sections={sections}
            locale={locale}
            onDeleteRequest={onDeleteRequest}
            onLayoutChange={handleLayoutChange}
            onLabelChange={handleLabelChange}
            writeSections={writeSections}
            baseStep={step}
            disabled={disabled}
            projectId={projectId}
            onboardingId={onboardingId}
            versionId={versionId}
            availableLocales={availableLocales}
            flowDocument={flowDocument}
            onContentChange={onChange}
          />
        )}
      />

      {/* Divider between sortable sections and static SAccordion sections */}
      <Divider sx={{ my: 2 }} />
    </Box>
  );
};
